import { useState } from "react";
import { Dialog } from "@headlessui/react";

import Input from "../../controls/Input";

export default function ScannedItemAmountModal(props) {
  const { receivingItem, setOpen, onConfirm, lotNumber, requiredAmount } =
    props;

  const [useFullAmount, setUseFullAmount] = useState(false);
  const [amount, setAmount] = useState(requiredAmount);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setError("");

    // Validate amount
    const finalAmount = useFullAmount
      ? parseFloat(receivingItem.received_amount)
      : parseFloat(amount);

    if (isNaN(finalAmount) || finalAmount <= 0) {
      setError("Amount must be greater than 0");
      return;
    }

    const maxAmount = parseFloat(receivingItem.received_amount);
    if (finalAmount > maxAmount) {
      setError(
        `Amount cannot exceed ${maxAmount.toFixed(2)} ${receivingItem.unit_of_measure || ""}`,
      );
      return;
    }

    // Prepare item with the specified amount
    const itemWithAmount = {
      production_id: lotNumber.value,
      barcode: receivingItem.barcode,
      used_amount: finalAmount,
    };

    try {
      setIsSubmitting(true);
      await Promise.resolve(onConfirm(itemWithAmount));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setOpen({
      childComponent: null,
      open: false,
    });
  };

  const handleCheckboxChange = (e) => {
    setUseFullAmount(e.target.checked);
    if (e.target.checked) {
      // When checked, set amount to full received_amount
      setAmount(parseFloat(receivingItem.received_amount));
      setError("");
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    setError("");
  };
  
  return (
    <div>
      <div className="mt-3 text-center sm:mt-5">
        <Dialog.Title
          as="h3"
          className="text-medium font-bold leading-6 text-gray-900"
        >
          {receivingItem?.product_name || "N/A"}
        </Dialog.Title>
        <Dialog.Title
          as="h3"
          className="text-base font-semibold leading-6 text-gray-900"
        >
          LOT: {receivingItem?.lot_number || "N/A"}
        </Dialog.Title>

        <Dialog.Title
          as="h3"
          className="text-base font-semibold leading-6 text-gray-900"
        >
          Barcode: {receivingItem?.barcode.slice(-9) || "N/A"}
        </Dialog.Title>
        <div className="mt-4 text-left">
          <div className="mb-4">
            <p className="text-sm text-gray-600 font-semibold mt-1">
              Available amount: {receivingItem?.received_amount || 0}{" "}
              {receivingItem?.unit_of_measure || ""}
            </p>
          </div>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={useFullAmount}
                onChange={handleCheckboxChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700 font-semibold">
                Use full amount
              </span>
            </label>
          </div>

          {!useFullAmount && (
            <div className="mb-4">
              <Input
                label="Amount"
                name="amount"
                type="number"
                step="0.01"
                min="0"
                max={receivingItem?.received_amount || 0}
                value={amount}
                onChange={handleAmountChange}
                labelOn={true}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
          )}

          {error && !useFullAmount && (
            <p className="text-sm text-red-500 font-semibold mt-1">{error}</p>
          )}
        </div>
      </div>
      <div className="mt-5 sm:mt-6 grid grid-cols-2 gap-3">
        <div>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:col-start-2 sm:mt-0"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
        <div>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed sm:col-start-2 sm:mt-0"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {isSubmitting ? "Adding..." : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
